import React, { useState } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';

const MONTHS = [
  { value: '', label: 'All Months' },
  { value: '4',  label: 'April'     }, { value: '5',  label: 'May'       },
  { value: '6',  label: 'June'      }, { value: '7',  label: 'July'      },
  { value: '8',  label: 'August'    }, { value: '9',  label: 'September' },
  { value: '10', label: 'October'   }, { value: '11', label: 'November'  },
  { value: '12', label: 'December'  }, { value: '1',  label: 'January'   },
  { value: '2',  label: 'February'  }, { value: '3',  label: 'March'     },
];

const WEEKS = Array.from({ length: 52 }, (_, i) => ({ value: String(i + 1), label: `Week ${i + 1}` }));

// Return local date as YYYY-MM-DD (avoids UTC-shift bug)
function localToday() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

export default function FilterBar({ filters, onChange, showWeek = true, showMonth = true, showDateRange = true, showToday = true }) {
  const [mode, setMode] = useState('month'); // 'today' | 'month' | 'week' | 'range'

  const update = (key, val) => {
    const next = { ...filters };
    if (key === 'mode') {
      setMode(val);
      next.week = ''; next.month = ''; next.startDate = ''; next.endDate = '';
      // For 'today': pre-fill both date fields with today
      if (val === 'today') { const t = localToday(); next.startDate = t; next.endDate = t; }
    } else {
      next[key] = val;
    }
    onChange(next);
  };

  return (
    <div className="flex flex-wrap items-center gap-3 bg-white rounded-xl px-4 py-3 shadow-card border border-slate-100">
      {/* Mode tabs */}
      <div className="flex bg-slate-100 rounded-lg p-0.5 gap-0.5">
        {showToday     && <TabBtn active={mode==='today'} label="Today"   onClick={() => update('mode','today')} />}
        {showMonth     && <TabBtn active={mode==='month'} label="Monthly" onClick={() => update('mode','month')} />}
        {showWeek      && <TabBtn active={mode==='week'}  label="Weekly"  onClick={() => update('mode','week')} />}
        {showDateRange && <TabBtn active={mode==='range'} label="Range"   onClick={() => update('mode','range')} />}
      </div>

      {/* Today indicator */}
      {mode === 'today' && (
        <span className="text-xs font-medium text-primary-700 bg-primary-50 border border-primary-100 px-3 py-1.5 rounded-lg flex items-center gap-1.5">
          <Calendar size={13}/>
          {new Date().toLocaleDateString('en-IN', { weekday:'short', day:'2-digit', month:'short', year:'numeric' })}
        </span>
      )}

      {/* Month selector */}
      {mode === 'month' && (
        <div className="relative">
          <select
            value={filters.month || ''}
            onChange={e => update('month', e.target.value)}
            className="appearance-none pl-3 pr-8 py-1.5 border border-slate-200 rounded-lg text-sm bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
          >
            {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
          <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        </div>
      )}

      {/* Week selector */}
      {mode === 'week' && (
        <div className="relative">
          <select
            value={filters.week || ''}
            onChange={e => update('week', e.target.value)}
            className="appearance-none pl-3 pr-8 py-1.5 border border-slate-200 rounded-lg text-sm bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
          >
            <option value="">All Weeks</option>
            {WEEKS.map(w => <option key={w.value} value={w.value}>{w.label}</option>)}
          </select>
          <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        </div>
      )}

      {/* Date Range */}
      {mode === 'range' && (
        <div className="flex items-center gap-2">
          <Calendar size={16} className="text-slate-400" />
          <input
            type="date"
            value={filters.startDate || ''}
            onChange={e => update('startDate', e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
          />
          <span className="text-slate-400 text-sm">to</span>
          <input
            type="date"
            value={filters.endDate || ''}
            onChange={e => update('endDate', e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
          />
        </div>
      )}
    </div>
  );
}

function TabBtn({ active, label, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${
        active ? 'bg-white text-primary-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'
      }`}
    >
      {label}
    </button>
  );
}
